import type { WallVerticalDatumAnchor, VerticalJustification } from "building-types/datums/datumsTypesEnum";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { InteriorMaterialOverride, ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { OpeningSide } from "building-types/openings/openingTypes";
import type { Formula, RemoveFormula } from "building-types/options/formula";
import type { ProfileId } from "building-types/profiles/profileTypes";
import type { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import type { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import type { Table } from "common/data/table";
import type { Face } from "geom/brep";
import type { NodeBSP } from "geom/bspTypes";
import type GeomTagGroup from "geom/geomTagGroup";
import type { Plane } from "geom/planeTypes";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { DocEntityHandle } from "../handles/docEntityHandleTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { EvaluatedRoofSlopeOptions, FriezeBoard, RoofSlopeOptions } from "./roofBaseTypes";
import type { EaveBracketPlacement, RoofTruncationType, TruncationSlopeType } from "./roofBaseTypes";
import type { RoofSettings } from "./roofSettings";
import type { EaveCutType, RoofPeakDirection, WrapFriezeBoardMode } from "./settingsTypesEnum";

export enum RoofSide {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
  START = "START",
  END = "END",
  INVALID = "INVALID",
}

export enum WallRoofBaseBoardMode {
  NONE = "NONE",
  ROOF_TOP_PLUMB_CUT = "ROOF_TOP_PLUMB_CUT",
  ROOF_TOP_SQUARED_CUT = "ROOF_TOP_SQUARED_CUT",
  FULL = "FULL",
}

export enum WallRoofBaseBoardFilter {
  ALL_EDGES = "ALL_EDGES",
  HORIZONTAL_EDGES = "HORIZONTAL_EDGES",
  SLOPED_EDGES = "SLOPED_EDGES",
}

export enum RoofFaces {
  ALL = "ALL", //gable face, wall face
  FRONT_AND_BACK = "FRONT_AND_BACK", //gable face
  LEFT_AND_RIGHT = "LEFT_AND_RIGHT", //wall face
  FRONT = "FRONT", //gable face
  BACK = "BACK", //gable face
  LEFT = "LEFT", //wall face
  RIGHT = "RIGHT", //wall face
}

export enum DormerRoofType {
  GABLE = "GABLE",
  SHED = "SHED",
}

export enum BuildingSide {
  FRONT = "FRONT",
  BACK = "BACK",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
  DIAGONAL = "DIAGONAL",
}

export interface InputParamToResolveEdgeSideOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly roofSettings: RoofSettings;
  readonly roofSideOverrides?: EvaluatedRoofSideOverridesTable;
  readonly roofEdgeOverrides?: EvaluatedRoofEdgeOverridesTable;
  readonly heelHeightOffsets: RoofHeelHeightOffsets;
  readonly legacyRoofSlope?: boolean;
  readonly legacyHeelHeightOffsets?: boolean;
}

export interface IsRoofEdgeAlignedToSideOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly side: RoofSide;
  readonly edgeIndex: number;
}

/**
 * RoofInputPerimeters stores all the needed perimters for the roof generation.
 * TODO: add more description about sub-fields.
 */
export interface RoofInputPerimeters {
  readonly inputPerimeter: PolyLine2;
  readonly srcPerimeter: PolyLine2;
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly bottomPerimeter: PolyLine2 | null;
  readonly limitPerimeter: PolyLine2 | null;
  readonly isVirtualRoof: boolean;
  readonly isFloatingVirtualRoof: boolean;
}

/**
 * RectangularOverhangs stores the overhangs for the main roof sides
 */
export interface RectangularOverhangs {
  readonly startOverhang: number;
  readonly endOverhang: number;
  readonly leftOverhang: number;
  readonly rightOverhang: number;
  readonly baseStartOverhang: number;
  readonly baseEndOverhang: number;
  readonly baseLeftOverhang: number;
  readonly baseRightOverhang: number;
}

/**
 * Stores all perimeters for the generated roof
 */
export interface RoofPerimeters {
  readonly startEdge: number;
  readonly peakDirection: RoofPeakDirection | undefined;
  readonly inputPerimeter: PolyLine2;
  readonly boundingPerimeter: PolyLine2;
  readonly offsetBoundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly offsetExactPerimeter: PolyLine2;
  readonly wallPerimeter: PolyLine2;
  readonly offsetWallPerimeter: PolyLine2;
  readonly eavesPerimeter: PolyLine2;
  readonly wallsPerimeterWRTOverhangDatum: PolyLine2;
}

/**
 * Stores information about the different sub-roofs from a level,
 * along with some extra infromation from the upper floor.
 * TODO: add more description about sub-fields.
 */
export interface RoofSystemLevel {
  readonly upperFloorBSP: NodeBSP | null;
  readonly upperFloorRoomsBSP: NodeBSP | null;
  readonly halfStoriesBSP: NodeBSP | null;
  readonly upperFloorPerimeters: Table<PolyLine2[]>;
  readonly upperFloorCeilElevation: number | undefined;
  readonly roofPerimeters: Table<RoofInputPerimeters>;
  readonly roofPlanarGraphs: PolygonPlanarGraph;
  readonly roofExactPlanarGraph: PolygonPlanarGraph;
  readonly roofIdToPolygonIndex: Table<number>;
  readonly roofPolygonIndexToRoofId: Table<string>;
}

/**
 * Slope instance for a roof.
 */
export interface RoofSlope {
  readonly slope: number;
  readonly horizontalDist: number;
  readonly startDist: number;
  readonly endDist: number;
  readonly startHeight: number;
  readonly endHeight: number;
  readonly hasStartOverlapping: boolean;
  readonly hasEndOverlapping: boolean;
}

/**
 * NeighborRoof is used to store information about a neighbor roof
 * in a specific side of the roof.
 */
export interface NeighborRoof {
  readonly isTruncated: boolean;
  readonly slopes: RoofSlope[];
  readonly oppSlopes: RoofSlope[];
  readonly roofId: string;
  readonly side: RoofSide;
  readonly baseHeight: number;
  readonly baseHeightOffset: number;
  readonly overhang: number;
  readonly startOverhang: number;
  readonly endOverhang: number;
  readonly relativeDist: number;
  readonly depth: number;
}

/**
 * NeighborRoofs is used to store information about the neighbor roofs
 * in all sides of the roof.
 */
export interface NeighborRoofs {
  readonly startNeighbor: NeighborRoof;
  readonly endNeighbor: NeighborRoof;
  readonly leftNeighbor: NeighborRoof;
  readonly rightNeighbor: NeighborRoof;
}

/**
 * RoofHeelHeightOffsets is used to store the default heel height offsets for a roof and
 * the overriden offsets for the left and right sides.
 */
export interface RoofHeelHeightOffsets {
  readonly refOffset: number;
  readonly leftOffset: number;
  readonly rightOffset: number;
  readonly startOffset: number;
  readonly endOffset: number;
}

/**
 * RoofEdgeOverrides defines the values we can override for a specific edge of
 * the roof.
 */

export interface RoofEdgeOverrides {
  readonly overhang?: number;
  readonly slope?: number | Formula;
  readonly slopeOptions?: RoofSlopeOptions[];
  readonly truncationType?: RoofTruncationType;
  readonly truncationSlopeType?: TruncationSlopeType;
  readonly truncationSlope?: number | Formula;
  readonly framingType?: RoofWallFramingType;
  readonly includeTriangleGableVent?: boolean;
  readonly triangleGableVentBaseWidth?: number | Formula;
  readonly includeFasciaTopTrim?: boolean;
  readonly fasciaTopTrimSize?: number;
  readonly gableBellyBandBoard?: ProfileId;
  readonly gableBellyBandBoardMaterial?: ExteriorMaterial;
  readonly gableBellyBandBoardAnchor?: WallVerticalDatumAnchor;
  readonly gableBellyBandBoardJustification?: VerticalJustification;
  readonly gableBellyBandBoardOffset?: number;
  readonly friezeBoard?: FriezeBoard[];
  readonly friezeBoardMaterialLegacy?: ExteriorMaterial; //to be removed several versions later, introduced in migration_374
  readonly includeFriezeBoardLegacy?: boolean | ProfileId; //to be removed several versions later, introduced in migration_374
  readonly wrapFriezeBoardMode?: WrapFriezeBoardMode;
  readonly includeRakeBrackets?: boolean;
  readonly rakeBracketPropId?: BuiltInExteriorPropId | ForeignEntityId<"PROP">;
  readonly rakeBracketAtPeak?: boolean;
  readonly rakeBracketParams?: number[];
  readonly rakeBracketParamsNormalized?: boolean;
  readonly rakeBracketTruncationParams?: number[];
  readonly soffitMaterial?: ExteriorMaterial;
  readonly fasciaMaterial?: ExteriorMaterial;
  readonly fasciaTopTrimMaterial?: ExteriorMaterial;
  readonly fasciaSize?: number;
  readonly soffitOffset?: number;
  readonly includeEaveBrackets?: boolean;
  readonly eaveBracketPropId?: BuiltInExteriorPropId | ForeignEntityId<"PROP">;
  readonly eaveBracketParams?: number[];
  readonly eaveBracketParamsNormalized?: boolean;
  readonly eaveBracketPlacement?: EaveBracketPlacement;
  readonly eaveCutType?: EaveCutType;
  readonly heelHeightOffset?: number | Formula;
  readonly normalizedHeelHeightOffset?: boolean;
  // TODO: add support to override more properties
}

export type EvaluatedRoofEdgeOverrides = RemoveFormula<RoofEdgeOverrides> & {
  readonly slopeOptions?: EvaluatedRoofSlopeOptions[];
};

/**
 * RoofSideOverridesTable is used to store the overrides for the different sides
 * (front, back, left or right) of the roof.
 */
export type RoofSideOverridesTable = { [key in RoofSide]?: RoofEdgeOverrides };

export type EvaluatedRoofSideOverridesTable = { [key in RoofSide]?: EvaluatedRoofEdgeOverrides };

/**
 * RoofEdgeOverridesTable is used to store the overrides for the different edges
 * of the roof (designed for free-form roofs).
 */
export type RoofEdgeOverridesTable = { [key: number]: RoofEdgeOverrides };

export type EvaluatedRoofEdgeOverridesTable = { [key: number]: EvaluatedRoofEdgeOverrides };

export const defaultRoofEdgeOverrides: EvaluatedRoofEdgeOverridesTable = {};

export interface SlopeOverlapRange {
  readonly t1: number;
  readonly t2: number;
}

/**
 * RoofTruncationOptions stores the input parameters for the roof truncation of
 * one side of the roof.
 */
export interface RoofTruncationOptions {
  readonly type: RoofTruncationType;
  readonly heightFactor: number;
  readonly normalizedHeight: boolean;
  readonly slopeType: TruncationSlopeType;
  readonly slopeOptions: RoofSlopeOptions[];
}

/**
 * Opening data for a skylight.
 */
export interface SkylightOpening {
  readonly frontHole: OpeningSide;
  readonly backHole: OpeningSide;
  readonly frontOutline3: PolyLine3;
  readonly backOutline3: PolyLine3;
  readonly frontPlane: Plane;
  readonly backPlane: Plane;
}

/**
 * Inserted skylight.
 */
export interface Skylight {
  readonly integerId: number;
  readonly hostSubRoofId: ForeignEntityId<"ROOF">;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly width: number;
  readonly height: number;
  readonly verticalOffset: number;
  readonly param: number;
  readonly paramNormalized?: boolean;
  readonly opening?: SkylightOpening;
}

/**
 * Inserted skylight unit (geometry of the skylight).
 */
export interface SkylightUnit {
  readonly skylight: Skylight;
  readonly glass: Face[];
  readonly hideInteriorFaces: Face[];
  readonly interior: GeomTagGroup<InteriorMaterialOverride>;
  readonly exterior: GeomTagGroup<ExteriorMaterialOverride>;
  readonly decorations: GeomTagGroup<ExteriorMaterialOverride>;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
}

/**
 * RidgeRail stores information about one ridge of the roof. It can be an horizontal ridge
 * (for the peak), or a sloped ridge (for the hips/truncations).
 */
export interface RidgeRail {
  readonly rail: PolyLine3;
  readonly ventProfile: PolyLine2;
  readonly capProfile: PolyLine2;
  readonly leftSlope: number;
  readonly rightSlope: number;
  readonly baseHeight: number;
  readonly isHorizontal: boolean;
  readonly addRidgeVent: boolean;
  readonly clippingPlanes: Plane[];
  clippedRails: PolyLine3[]; // TODO make readonly
  ridgeVentRails: PolyLine3[]; // TODO make readonly
}

/**
 * RoofRails stores the different rails used for generating the roof, plus some extra
 * rails for ridges, eaves and rakes.
 */
export interface RoofRails {
  readonly leftRail: PolyLine3;
  readonly rightRail: PolyLine3;
  readonly startRail: PolyLine3;
  readonly endRail: PolyLine3;
  readonly peakRail: PolyLine3;
  readonly peakRailNoExtension: PolyLine3;
  readonly ridgeRails: RidgeRail[];
  readonly returnPeakRails: PolyLine3[];
  readonly returnEaveRails: PolyLine3[];
}

/**
 * RoofRails2D stores the different 2D rails used for generating the roof. Some
 * of the 3D rails from RoofRails are generated by projecting these 2D rails into
 * the corresponding planes.
 */
export interface RoofRails2D {
  readonly leftRail: PolyLine2;
  readonly rightRail: PolyLine2;
  readonly startRail: PolyLine2;
  readonly endRail: PolyLine2;
  readonly peakRail: PolyLine2;
  readonly peakRailNoExtension: PolyLine2;
}

/**
 * RoofConnectivity stores some information about one end of the roof.
 * isFree -> the end is not connected to any other roof
 * isTouchingBuilding -> the end is connected to a building
 * hasPartialOverlap -> the end is connected to a roof, but only partially
 */
export interface RoofConnectivity {
  readonly isFree: boolean;
  readonly isTouchingBuilding: boolean;
  readonly hasPartialOverlap: boolean;
}

/**
 * RoofExtensions stores all the information for extending the roof, for
 * connecting it with other roofs. Side extension is only used for connected
 * shed roofs
 */
export interface RoofExtensions {
  readonly hasStartExtension: boolean;
  readonly hasEndExtension: boolean;
  readonly hasStartPartialOverlap: boolean;
  readonly hasEndPartialOverlap: boolean;
  readonly startLeftExtensions: number[];
  readonly startRightExtensions: number[];
  readonly endLeftExtensions: number[];
  readonly endRightExtensions: number[];
}

/**
 * ShedRoofExtensions stores the extension information for a shed roof.
 */
export interface ShedRoofExtensions {
  readonly hasLeftExtension: boolean;
  readonly hasRightExtension: boolean;
  readonly sideExtension: number;
}

/**
 * RoofOverhangs stores overhang sizes for the different roof sizes.
 * The ones with the "base" prefix are the default overhangs don't include
 * the exteriorWallOffsetWithSheathing
 */
export interface RoofOverhangs {
  readonly edgeOverhangs: number[];
  readonly baseEdgeOverhangs: number[];
  readonly limitEdgeOverhangs: number[];
  readonly limitEdgeNoOverhangs: number[];
}

/**
 * RoofGableFaces stores the faces for a gable roof end.
 * face -> the wall face of the gable
 * discardFace -> the wall face offset by the overhang
 */
export interface RoofGableFaces {
  plane: Plane | null;
  face: Face | null;
  discardFace: Face | null;
}

/**
 * RoofGableVents stores the vent information gable roof start and end.
 */
export interface RoofGableVents {
  readonly includeStartTriangleGableVent?: boolean;
  readonly startTriangleGableVentBaseWidth?: number;
  readonly includeEndTriangleGableVent?: boolean;
  readonly endTriangleGableVentBaseWidth?: number;
}

export interface RoofMaterialTrimAndProps {
  readonly includeFasciaTopTrim?: boolean;
  readonly fasciaTopTrimSize?: number;
  readonly fasciaTopTrimMaterial?: ExteriorMaterial;
  readonly friezeBoard?: FriezeBoard[];
  readonly friezeBoardMaterialLegacy?: ExteriorMaterial;
  readonly wrapFriezeBoardMode?: WrapFriezeBoardMode;
  readonly includeRakeBrackets?: boolean;
  readonly rakeBracketPropId?: BuiltInExteriorPropId | ForeignEntityId<"PROP">;
  readonly rakeBracketAtPeak?: boolean;
  readonly rakeBracketParams?: number[];
  readonly rakeBracketParamsNormalized?: boolean;
  readonly rakeBracketTruncationParams?: number[];
  readonly soffitMaterial?: ExteriorMaterial;
  readonly fasciaMaterial?: ExteriorMaterial;
  readonly fasciaSize?: number;
  readonly soffitOffset?: number;
  readonly includeEaveBrackets?: boolean;
  readonly eaveBracketPropId?: BuiltInExteriorPropId | ForeignEntityId<"PROP">;
  readonly eaveBracketParams?: number[];
  readonly eaveBracketParamsNormalized?: boolean;
  readonly eaveBracketPlacement?: EaveBracketPlacement;
}
